import { queryOptions } from '@w/react-query/db'
import type { QueryClient } from '@w/react-query'
import { glossUnits, validateTranslatedUnit, type TranslationUnit } from '@w/content-blocks'
import { pageTranslationUnits } from './page-title-unit'
import type { PageRow } from '../db/page-read'
import type { FlashcardsFeRouterCtx } from '../router/flashcards-fe.router-ctx'
import { safe } from '@w/utils'
import type { Tel } from '@w/tel'
import { decodeReadable } from '../dead-letter-queue/item-readers'

export type TranslationLookup = (input: { texts: string[]; target: string; format: 'text' | 'html' }) => Promise<{ translations: ({ text: string; detectedSourceLanguage?: string } | null)[] }>
export type PageTranslationRow = { pageId: string; blockId: string; language: string; sourceHash: string; text: string | null; detectedSourceLanguage: string | null }

export function readTranslationRows(page: PageRow, language: string, lookup: TranslationLookup): Promise<PageTranslationRow[]> {
  return readManyTranslationRows([page], language, lookup)
}

export async function readManyTranslationRows(pages: PageRow[], language: string, lookup: TranslationLookup): Promise<PageTranslationRow[]> {
  const live = pages.filter(page => !page.deletedAt && (page.content.kind === 'note' || page.content.kind === 'language'))
  const rows: PageTranslationRow[] = []
  for (const format of ['html', 'text'] as const) {
    const units: { page: PageRow; blockId: string; hash: string; text: string; unit?: TranslationUnit }[] = live.flatMap(page => {
      if (page.content.kind !== 'note' && page.content.kind !== 'language') return []
      return format === 'html'
        ? pageTranslationUnits({ title: page.title, children: page.content.children }).map(unit => ({ page, blockId: unit.blockId, hash: unit.hash, text: unit.html, unit }))
        : glossUnits(page.content.children).map(unit => ({ page, blockId: unit.blockId, hash: unit.hash, text: unit.text }))
    })
    if (units.length === 0) continue
    const { translations } = await lookup({ texts: units.map(unit => unit.text), target: language, format })
    if (translations.length !== units.length) throw new Error('Translation lookup result count does not match input')
    units.forEach((unit, index) => {
      const result = translations[index]
      if (!result) return
      rows.push({ pageId: unit.page.id, blockId: unit.blockId, language, sourceHash: unit.hash,
        text: unit.unit && !validateTranslatedUnit(unit.unit, result.text) ? null : result.text,
        detectedSourceLanguage: result.detectedSourceLanguage ?? null })
    })
  }
  return rows
}

export function manyPageTranslationRowsOptions(ctx: FlashcardsFeRouterCtx, pages: PageRow[], language: string) {
  const readers = pages.map(page => pageTranslationRowsOptions(ctx, page, language))
  return { subscribe: async (invalidate: () => void) => {
      const releases = await Promise.all(readers.map(reader => Promise.resolve(reader.subscribe?.(invalidate))))
      return () => { for (const release of releases) release?.() }
    }, queryKey: ['workspace-translation-rows', ctx.getUser().id, language, pages.map(page => [page.spaceId, page.id])], staleTime: Infinity,
    queryFn: async () => (await Promise.all(readers.map(reader => reader.queryFn()))).flat() }
}

export function pageTranslationRowsOptions(ctx: FlashcardsFeRouterCtx, page: PageRow, language: string) {
  const db = ctx.db(page.spaceId)
  const query = db.query`SELECT ${db.pageTranslations.all} FROM ${db.pageTranslations}
    WHERE ${db.pageTranslations.pageId.eq(page.id)} AND ${db.pageTranslations.language.eq(language)}
    ORDER BY ${db.pageTranslations.blockId}`
  const options = queryOptions(query, db.pageTranslations.__selectSchema, { staleTime: Infinity })
  const { sql, params } = query.toSql()
  return { ...options, queryFn: async (qctx?: { tel?: Tel }) => decodeReadable(await safe(db.driver).query<Record<string, unknown>>(sql, params, qctx), db.pageTranslations.__selectSchema) }
}

/** Maintenance writes invalidate both live SQL readers and composite page snapshots. */
export function refreshTranslationRows(client: QueryClient): void {
  void client.invalidateQueries({ predicate: query => query.queryKey[0] === 'page-translation-rows' || query.queryKey[0] === 'workspace-translation-rows' || typeof query.queryKey[0] === 'string' && /\bpageTranslations\b/i.test(query.queryKey[0]) }).catch(() => {})
}
